/**
 * DashboardPage Component
 *
 * New dashboard showing:
 * - Hero section with recording button
 * - Process explanation cards
 * - Today's appointments with real-time data
 */

import { CAPABILITIES } from '@/lib/types/authorization';
import { useCan } from '@/lib/hooks/useCan';
import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AppLayout } from '@/components/layout/AppLayout';
import {
  DashboardView,
  AppointmentRowData,
  DashboardStatistics,
} from '@/components/views/DashboardView';
import { PatientFormModal } from '@/components/modals/PatientFormModal';
import { PatientFormContent } from '@/components/forms/PatientFormContent';
import {
  ScheduleAppointmentModal,
  PatientData,
} from '@/components/modals/ScheduleAppointmentModal';
import { PatientSearchModal } from '@/components/modals/PatientSearchModal';
import { medplumResourcesApi } from '@/lib/api/medplum-resources';
import appointmentsApi from '@/lib/api/appointments';
import { draftsApi } from '@/lib/api/drafts';
import { isDeleteLocked } from '@/components/shared/AppointmentsTable';
import { caseSheetSummaryApi } from '@/lib/api/case-sheet-summary';
import { statisticsApi } from '@/lib/api/statistics';
import { useAuthStore } from '@/lib/stores/authStore';
import { createComponentLogger } from '@/lib/utils/logger';
import { calculateAge, formatGenderDisplay } from '@/lib/types/patient';
import {
  getEncounterForAppointment,
  linkEncounterToAppointment,
  updateAppointmentStatusSafe,
  startConsultationForPatient,
} from '@/lib/utils/encounter';

const logger = createComponentLogger('DashboardPage');

// Items per page for dashboard appointments table
const ITEMS_PER_PAGE = 10;

export function DashboardPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useAuthStore((state) => state.user);
  const projectConfig = useAuthStore((state) => state.projectConfig);

  // Administrators may view all appointments when enabled for the project.
  const canConsult = useCan(CAPABILITIES.CONSULTATION_START);
  const canViewAllAppointments = user?.role === 'admin';
  const effectiveShowDoctorFilter =
    canViewAllAppointments && projectConfig?.services.showDoctorUi !== false;
  const effectiveShowDoctorColumn = effectiveShowDoctorFilter;

  // Admin dashboard state
  const [adminStartDate, setAdminStartDate] = useState<Date>(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date;
  });
  const [adminEndDate, setAdminEndDate] = useState<Date>(new Date());

  // Fetch admin statistics
  const {
    data: adminStatistics,
    isLoading: isAdminStatsLoading,
    error: adminStatsError,
    refetch: refetchAdminStats,
  } = useQuery({
    queryKey: ['admin-statistics', adminStartDate.toISOString(), adminEndDate.toISOString()],
    queryFn: () =>
      statisticsApi.getOrganizationStatistics(
        adminStartDate.toISOString(),
        adminEndDate.toISOString()
      ),
    refetchInterval: 30000,
    staleTime: 5 * 60 * 1000,
    enabled: user?.role === 'admin',
  });

  const queryClient = useQueryClient();

  // Doctor filter state
  const [selectedDoctorId, setSelectedDoctorId] = useState<string | undefined>(undefined);

  // Get addPatient from URL query params for Add Patient modal
  const addPatientFromQuery = searchParams.get('addPatient');
  // For edit mode in form modal, check if addPatient is true and patientId exists
  const patientIdFromQuery = searchParams.get('patientId');
  const isEditModeInModal = addPatientFromQuery === 'true' && patientIdFromQuery !== null;
  // Get prefillPhone for when switching from appointment search to patient creation
  const prefillPhoneFromQuery = searchParams.get('prefillPhone');

  // Add Appointment flow state (two-step: search patient -> schedule)
  const [patientSearchModalOpen, setPatientSearchModalOpen] = useState(false);
  const [scheduleModalPatient, setScheduleModalPatient] = useState<PatientData | null>(null);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [isCreatingEncounter, setIsCreatingEncounter] = useState<string | null>(null); // Track which appointment ID is being opened
  const [isContinuingConsultation, setIsContinuingConsultation] = useState<string | null>(null); // Track which appointment is being continued

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);

  // Selected stat card for filtering ('all' = total, 'new' = new patients, 'followup' = existing patients)
  const [selectedStatCard, setSelectedStatCard] = useState<'all' | 'new' | 'followup'>('all');

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
      setCurrentPage(1); // Reset to page 1 on search
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset page when doctor filter or stat card filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedDoctorId, selectedStatCard]);

  // Fetch practitioners list for the administrator doctor filter.
  const { data: practitionersData } = useQuery({
    queryKey: ['practitioners-for-filter'],
    queryFn: async () => {
      return medplumResourcesApi.listPractitioners({ count: 100 });
    },
    enabled: effectiveShowDoctorFilter,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Fetch today's appointments for statistics (replaces old encounter-based stats)
  const {
    data: todayStatsData,
    isLoading: isLoadingTodayStats,
    isError: isTodayStatsError,
    error: todayStatsError,
  } = useQuery({
    queryKey: ['dashboard-today-stats', user?.id, canViewAllAppointments ? selectedDoctorId : null],
    queryFn: async () => {
      if (!canViewAllAppointments && !user?.id) {
        throw new Error('User ID not available');
      }

      logger.log(`📊 Fetching today's appointments for stats`);

      // Build today's date range
      const today = new Date();
      const todayStart = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
        0,
        0,
        0,
        0
      );
      const todayEnd = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
        23,
        59,
        59,
        999
      );

      const params: any = {
        count: 1000, // Get all today's appointments
        dateStart: todayStart.toISOString(),
        dateEnd: todayEnd.toISOString(),
        status: ['booked', 'pending', 'proposed', 'arrived', 'checked-in', 'waitlist', 'fulfilled'],
        include: ['Appointment:patient'],
      };

      // Apply doctor filter for supervisor role
      if (canViewAllAppointments) {
        if (selectedDoctorId) {
          params.actor = `Practitioner/${selectedDoctorId}`;
        }
      } else {
        params.actor = `Practitioner/${user!.id}`;
      }

      const result = await appointmentsApi.listAppointments(params);

      // Extract unique patient IDs
      const patientIds = result.appointments
        .map((apt) => apt.patientId)
        .filter((id): id is string => !!id);

      // Check which patients are new (first appointment ever)
      const patientIsNewMap = await appointmentsApi.checkPatientsAreNew(patientIds);

      // Calculate stats
      let newConsultations = 0;
      let followUpVisits = 0;

      result.appointments.forEach((apt) => {
        if (apt.patientId) {
          const isNew = patientIsNewMap.get(apt.patientId);
          if (isNew) {
            newConsultations++;
          } else {
            followUpVisits++;
          }
        }
      });

      return {
        totalConsultations: result.appointments.length,
        newConsultations,
        followUpVisits,
        patientIsNewMap,
      };
    },
    enabled: canViewAllAppointments || !!user?.id,
    staleTime: 30000, // 30 seconds - stats update more frequently
  });

  // When a stat card filter is active (new/followup), fetch ALL today's appointments
  // so we can filter client-side across the full dataset, not just one page.
  // When 'all' is selected, use server-side pagination as before.
  const isFilterActive = selectedStatCard !== 'all';

  // Fetch today's appointments with server-side pagination, filtering, and search
  const {
    data: appointmentsData,
    isFetching: isFetchingAppointments,
    isError: isAppointmentsError,
    error: appointmentsError,
  } = useQuery({
    queryKey: [
      'dashboard-practitioner-appointments',
      'supervisor',
      effectiveShowDoctorFilter ? selectedDoctorId : null,
      isFilterActive ? 'all' : currentPage, // When filtering, fetch all (no pagination)
      debouncedQuery,
      isFilterActive, // Include filter state in query key
    ],
    queryFn: async () => {
      if (!user?.id) {
        throw new Error('User ID not available');
      }

      logger.log(`🚀 Fetching appointments (access-policy filtered)`);

      // Build today's date range
      const today = new Date();
      const todayStart = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
        0,
        0,
        0,
        0
      );
      const todayEnd = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
        23,
        59,
        59,
        999
      );

      // When a stat card filter is active, fetch all appointments (no pagination)
      // so we can filter client-side across the full dataset
      const count = isFilterActive ? 1000 : ITEMS_PER_PAGE;
      const offset = isFilterActive ? 0 : (currentPage - 1) * ITEMS_PER_PAGE;

      const params: Parameters<typeof appointmentsApi.listAppointments>[0] = {
        count,
        offset,
        sort: 'date',
        total: 'accurate',
        include: ['Appointment:patient', 'Appointment:actor'],
        dateStart: todayStart.toISOString(),
        dateEnd: todayEnd.toISOString(),
        status: ['booked', 'pending', 'proposed', 'arrived', 'waitlist'],
      };

      // Apply doctor filter
      if (effectiveShowDoctorFilter && selectedDoctorId) {
        params.actor = `Practitioner/${selectedDoctorId}`;
      }
      // Access policy handles what appointments the user can see — no explicit actor constraint needed

      // Server-side search
      if (debouncedQuery.trim()) {
        const isPhoneNumber = /^[\d\s\-+()]+$/.test(debouncedQuery.trim());
        if (isPhoneNumber) {
          params.patientPhone = debouncedQuery.trim();
        } else {
          params.patientName = debouncedQuery.trim();
        }
      }

      return appointmentsApi.listAppointments(params);
    },
    enabled: !!user?.id,
    staleTime: 30000,
    placeholderData: (previousData: any) => previousData,
  });

  // Use included patients and practitioners from the appointment response
  const patientsMap = appointmentsData?.patients;
  const practitionersMap = appointmentsData?.practitioners;

  // Get patient classification map from today's stats query
  const patientIsNewMap = todayStatsData?.patientIsNewMap;

  const appointmentRows: AppointmentRowData[] = useMemo(() => {
    if (!appointmentsData?.appointments) return [];

    const mapped = appointmentsData.appointments.map((appointment) => {
      const patient = appointment.patientId ? patientsMap?.get(appointment.patientId) : undefined;
      const isNewPatient = appointment.patientId
        ? (patientIsNewMap?.get(appointment.patientId) ?? false)
        : false;

      if (!patient) {
        // Return basic appointment data if patient not found
        return {
          id: appointment.id,
          patientName: appointment.patientName || 'Unknown',
          patientId: appointment.patientId || appointment.id,
          patientIdentifier: undefined,
          age: 0,
          gender: 'Other' as const,
          contact: 'N/A',
          visitType: mapAppointmentTypeToVisitType(appointment.appointmentType),
          visitTime: '',
          status: mapAppointmentStatusToDisplay(appointment.status),
          originalStatus: appointment.status,
          hasReport: false,
          appointmentStart: appointment.start,
          appointmentEnd: appointment.end,
          encounterId: appointment.encounterId,
          doctorName: appointment.practitionerName,
          practitionerId: appointment.practitionerId,
          isNewPatient,
        };
      }

      const age = patient.birthDate ? calculateAge(patient.birthDate) : 0;
      const gender = formatGenderDisplay(patient.gender);
      const phone =
        patient.telecom?.find((t: { system: string; value: string }) => t.system === 'phone')
          ?.value || 'N/A';
      const name = patient.name?.[0]
        ? `${patient.name[0].given?.join(' ') || ''} ${patient.name[0].family || ''}`.trim() ||
          patient.name[0].text ||
          appointment.patientName ||
          'Unknown'
        : appointment.patientName || 'Unknown';

      // Get practitioner name from included resources if available, fallback to API response
      let doctorName = appointment.practitionerName;
      if (appointment.practitionerId && practitionersMap) {
        const practitioner = practitionersMap.get(appointment.practitionerId);
        if (practitioner?.name?.[0]) {
          const pName = practitioner.name[0];
          doctorName =
            `${pName.given?.join(' ') || ''} ${pName.family || ''}`.trim() ||
            appointment.practitionerName;
        }
      }

      return {
        id: appointment.id,
        patientName: name,
        patientId: appointment.patientId || patient.id,
        patientIdentifier: patient.identifier?.[0]?.value || patient.id,
        age,
        gender,
        contact: phone,
        visitType: mapAppointmentTypeToVisitType(appointment.appointmentType),
        visitTime: '',
        status: mapAppointmentStatusToDisplay(appointment.status),
        originalStatus: appointment.status,
        hasReport: false,
        appointmentStart: appointment.start,
        appointmentEnd: appointment.end,
        encounterId: appointment.encounterId,
        doctorName,
        practitionerId: appointment.practitionerId,
        isNewPatient,
      };
    });

    // Apply client-side filtering when a stat card filter is active
    if (selectedStatCard === 'new') {
      return mapped.filter((row) => row.isNewPatient);
    }
    if (selectedStatCard === 'followup') {
      return mapped.filter((row) => !row.isNewPatient);
    }
    return mapped;
  }, [appointmentsData, patientsMap, practitionersMap, patientIsNewMap, selectedStatCard]);

  // Calculate pagination — use filtered count when filter is active
  const effectiveTotal = isFilterActive ? appointmentRows.length : appointmentsData?.total || 0;
  const totalPages = Math.ceil(effectiveTotal / ITEMS_PER_PAGE);

  // When filter is active, slice the filtered results for pagination
  const paginatedAppointmentRows = isFilterActive
    ? appointmentRows.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE)
    : appointmentRows;

  // Quick Submit status: only relevant for non-fulfilled appointments that
  // already have an encounter. Whether Quick Submit (vs a normal Submit) was
  // used is read from the pranikCdss.quickSubmit marker inside the local
  // case-sheet-summary row — that marker is set only by AudioRecorder's
  // Quick Submit handler (see ConsultationPage.tsx's handleSaveRecord), so a
  // normal Submit's leftover row is correctly ignored here. A Medplum
  // DiagnosticReport for the encounter then marks "ready to verify".
  const candidateEncounterIds = useMemo(
    () =>
      Array.from(
        new Set(
          paginatedAppointmentRows
            .filter((row) => row.encounterId && row.originalStatus !== 'fulfilled')
            .map((row) => row.encounterId as string)
        )
      ),
    [paginatedAppointmentRows]
  );

  const { data: summaryStatusMap } = useQuery({
    queryKey: ['dashboard-summary-status', candidateEncounterIds],
    queryFn: async () => {
      const map: Record<string, 'preparing' | 'pending_verification'> = {};
      await Promise.all(
        candidateEncounterIds.map(async (encounterId) => {
          // Once an encounter is confirmed ready, that's final — a
          // DiagnosticReport doesn't un-appear. Skip re-checking Medplum for
          // it on every poll; only genuinely unresolved encounters get
          // re-fetched. This cache entry is keyed by encounterId alone (not
          // by the whole candidate list), so it survives even as the list of
          // rows being watched changes.
          const reportReady = queryClient.getQueryData<'pending_verification'>([
            'quick-submit-report-ready',
            encounterId,
          ]);
          if (reportReady === 'pending_verification') {
            map[encounterId] = 'pending_verification';
            return;
          }

          // Once we've positively confirmed this encounter IS a Quick
          // Submit, remember that separately from "is it ready yet" — so a
          // later 404/error/race checking readiness can never make this row
          // silently fall out of the "preparing" bucket (which is what was
          // stopping polling prematurely: see the note on refetchInterval
          // below).
          const isConfirmedQuickSubmit = queryClient.getQueryData<true>([
            'quick-submit-confirmed',
            encounterId,
          ]);

          // Right after Quick Submit is clicked, the local record save is
          // fire-and-forget — this check can easily race it and 404 before
          // it lands. Treat anything clicked in roughly the last 2 minutes
          // as "assume Quick Submit, keep checking" rather than "not
          // applicable", so that race can't make the row go quiet forever.
          const justFiredAt = queryClient.getQueryData<number>([
            'quick-submit-just-fired',
            encounterId,
          ]);
          const recentlyFired = !!justFiredAt && Date.now() - justFiredAt < 2 * 60 * 1000;

          if (isConfirmedQuickSubmit) {
            // Skip the local-summary check entirely — go straight to
            // Medplum, defaulting to "preparing" no matter what happens.
            map[encounterId] = 'preparing';
            try {
              const reportReady =
                await medplumResourcesApi.hasConsultationSummaryForEncounter(encounterId);
              if (reportReady) {
                map[encounterId] = 'pending_verification';
                queryClient.setQueryData(
                  ['quick-submit-report-ready', encounterId],
                  'pending_verification'
                );
              }
            } catch (err) {
              logger.warn('Failed to check report status for encounter:', encounterId, err);
              // Leave as 'preparing' — already confirmed a Quick Submit, so
              // a transient failure here must not drop this row's status.
            }
            return;
          }

          try {
            const localSummary = await caseSheetSummaryApi.getSummary(encounterId);
            if (localSummary?.pranikCdss?.quickSubmit) {
              queryClient.setQueryData(['quick-submit-confirmed', encounterId], true);
              map[encounterId] = 'preparing';
              const reportReady =
                await medplumResourcesApi.hasConsultationSummaryForEncounter(encounterId);
              if (reportReady) {
                map[encounterId] = 'pending_verification';
                queryClient.setQueryData(
                  ['quick-submit-report-ready', encounterId],
                  'pending_verification'
                );
              }
              return;
            }
            // No confirmed marker yet. If this was Quick Submitted very
            // recently, assume the local save just hasn't landed yet rather
            // than concluding it's unrelated — keep it "preparing" so
            // polling continues instead of going quiet.
            if (recentlyFired) {
              map[encounterId] = 'preparing';
            }
            // Otherwise: genuinely not a Quick Submit — no entry, no status.
          } catch (err) {
            logger.warn('Failed to check summary status for encounter:', encounterId, err);
            if (recentlyFired) {
              map[encounterId] = 'preparing';
            }
          }
        })
      );
      return map;
    },
    enabled: candidateEncounterIds.length > 0,
    refetchInterval: (query) => {
      const map = query.state.data;
      if (!map) return 15000;
      const hasPreparing = Object.values(map).some((v) => v === 'preparing');
      return hasPreparing ? 15000 : false;
    },
  });

  const displayedAppointmentRows: AppointmentRowData[] = useMemo(
    () =>
      paginatedAppointmentRows.map((row) => {
        const summaryStatus = row.encounterId ? summaryStatusMap?.[row.encounterId] : undefined;
        return summaryStatus ? { ...row, summaryStatus } : row;
      }),
    [paginatedAppointmentRows, summaryStatusMap]
  );

  const isFetching = isFetchingAppointments;
  const isError = isAppointmentsError;
  const error = appointmentsError;

  const handleRecordClick = () => {
    toast.info('Redirecting to patient form to start recording...');
    navigate('/patient-form');
  };

  const handleViewDetails = (appointmentId: string) => {
    // Find the appointment to get the patient ID
    const appointment = appointmentRows.find((row) => row.id === appointmentId);
    if (appointment?.patientId) {
      navigate(`/patient-record/${appointment.patientId}`);
    } else {
      toast.error('Patient information not found');
    }
  };

  const handleViewReport = (appointmentId: string) => {
    // Find the appointment to get the patient ID
    const appointment = appointmentRows.find((row) => row.id === appointmentId);
    if (appointment?.patientId) {
      navigate(`/patient-record/${appointment.patientId}`);
    } else {
      toast.error('Patient information not found');
    }
  };

  const handleVerify = (encounterId: string) => {
    navigate(`/case-sheet/${encounterId}`);
  };

  const handleStartConsultation = async (appointmentId: string) => {
    if (!user) {
      toast.error('User not authenticated');
      return;
    }

    if (isCreatingEncounter) {
      return; // Prevent double-clicking
    }

    // Find the appointment to get the patient ID and encounter ID
    const appointment = appointmentRows.find((row) => row.id === appointmentId);
    if (!appointment?.patientId) {
      toast.error('Patient information not found');
      return;
    }

    // Show toast immediately on click
    toast.info('Opening consultation...', { duration: 2000 });
    setIsCreatingEncounter(appointmentId);

    // Fast path: If we already have the encounter ID from list data, navigate directly
    if (appointment.encounterId) {
      logger.log('Fast path: Using cached encounter ID:', appointment.encounterId);
      // Sequential to avoid read-modify-write race on the same Appointment resource
      await linkEncounterToAppointment(appointment.encounterId, appointmentId);
      await updateAppointmentStatusSafe(appointmentId, 'arrived');
      navigate(`/consultation/${appointment.encounterId}?appointmentId=${appointmentId}`);
      setIsCreatingEncounter(null);
      return;
    }

    // Slow path: Fetch encounter from appointment (PA flow or manual flow with vitals)
    try {
      logger.log('Fetching encounter for appointment:', appointmentId);
      const encounter = await getEncounterForAppointment(appointmentId);

      if (encounter?.id) {
        // Found encounter (PA flow or manual flow with vitals)
        logger.log('Found encounter:', encounter.id);

        // Sequential to avoid read-modify-write race on the same Appointment resource
        await linkEncounterToAppointment(encounter.id, appointmentId);
        await updateAppointmentStatusSafe(appointmentId, 'arrived');

        toast.success('Ready for consultation');
        navigate(`/consultation/${encounter.id}?appointmentId=${appointmentId}`);
        setIsCreatingEncounter(null);
      } else {
        // No encounter found for this appointment.
        // Use BFF to find or create today's Appointment + Encounter atomically.
        logger.log(
          'No encounter found, calling startConsultationForPatient for appointment:',
          appointmentId
        );

        try {
          const { encounterId } = await startConsultationForPatient(appointment.patientId);

          updateAppointmentStatusSafe(appointmentId, 'arrived');
          toast.success('Ready for consultation');
          navigate(`/consultation/${encounterId}?appointmentId=${appointmentId}`);
        } catch (createError) {
          logger.error('Failed to start consultation:', createError);
          toast.error('Failed to start consultation. Please try again.', { duration: 3000 });
        } finally {
          setIsCreatingEncounter(null);
        }
      }
    } catch (error) {
      logger.error('Error starting consultation:', error);
      toast.error('Failed to start consultation. Please try again.', { duration: 3000 });
      setIsCreatingEncounter(null);
    }
  };

  const handleAddPatient = () => {
    setSearchParams({ addPatient: 'true' });
  };

  const handleAddAppointment = () => {
    setPatientSearchModalOpen(true);
  };

  const handlePatientSelected = (patient: PatientData) => {
    setScheduleModalPatient(patient);
  };

  const handleAppointmentCreated = () => {
    // Invalidate and refetch appointments (use partial match to cover all query key variants)
    queryClient.invalidateQueries({
      queryKey: ['dashboard-practitioner-appointments'],
      exact: false,
    });
    queryClient.invalidateQueries({ queryKey: ['appointments'], exact: false });
    queryClient.invalidateQueries({ queryKey: ['dashboard-patients'], exact: false });
    setScheduleModalPatient(null);
  };

  const handleAppointmentUpdated = () => {
    // Invalidate and refetch appointments (use partial match to cover all query key variants)
    queryClient.invalidateQueries({
      queryKey: ['dashboard-practitioner-appointments'],
      exact: false,
    });
    queryClient.invalidateQueries({ queryKey: ['appointments'], exact: false });
  };

  const handleContinueConsultation = async (appointmentId: string, encounterId: string) => {
    if (isContinuingConsultation) return;

    setIsContinuingConsultation(appointmentId);
    toast.info('Loading previous session...', { duration: 2000 });

    try {
      // Fetch saved CDSS data from database
      const draft = await draftsApi.getDraft(encounterId);

      const previousSummary = draft?.cdssData ? JSON.stringify(draft.cdssData) : '{}';

      navigate(`/consultation/${encounterId}?appointmentId=${appointmentId}`, {
        state: { isContinuation: true, previousSummary },
      });
    } catch (error) {
      logger.error('Failed to continue consultation:', error);
      toast.error('Failed to load previous session. Please try again.');
    } finally {
      setIsContinuingConsultation(null);
    }
  };

  const handleDeleteAppointment = async (appointmentId: string) => {
    // Defense in depth: the delete button is already disabled in the UI for
    // this case, but guard here too so nothing can delete an appointment
    // whose consultation is started and on hold.
    const appointment = appointmentRows.find((row) => row.id === appointmentId);
    if (isDeleteLocked(appointment?.originalStatus, appointment?.encounterId)) {
      toast.error('Cannot delete — consultation is on hold. Finish or cancel it first.');
      return;
    }

    try {
      await appointmentsApi.deleteAppointment(appointmentId);
      toast.success('Appointment deleted successfully');
      // Invalidate and refetch appointments (use partial match to cover all query key variants)
      queryClient.invalidateQueries({
        queryKey: ['dashboard-practitioner-appointments'],
        exact: false,
      });
      queryClient.invalidateQueries({ queryKey: ['appointments'], exact: false });
    } catch (error) {
      logger.error('Failed to delete appointment:', error);
      toast.error('Failed to delete appointment');
      throw error;
    }
  };

  // Close form modal handler
  const handleCloseFormModal = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('addPatient');
    newParams.delete('prefillPhone');
    // Only delete patientId if we're in create mode (not edit mode)
    if (!isEditModeInModal) {
      newParams.delete('patientId');
    }
    setSearchParams(newParams);
  };

  // Handle form submission success
  const handleFormSuccess = () => {
    handleCloseFormModal();
  };

  // Handle switching from Add Patient form to Schedule Appointment
  // This is called when a patient already exists and user wants to schedule an appointment
  const handleSwitchToAppointment = (patient: {
    id: string;
    name: string;
    phoneNumber: string;
    age?: number;
    gender: 'M' | 'F' | 'O' | 'U';
  }) => {
    // Close the patient form modal
    handleCloseFormModal();

    // Set the patient for scheduling
    setScheduleModalPatient({
      uuid: patient.id,
      name: patient.name,
      age: patient.age ?? 0,
      gender: patient.gender,
      phoneNumber: patient.phoneNumber,
    });
  };

  // Handle switching from Patient Search to Add Patient form
  // This is called when a patient is not found and user wants to create a new patient
  const handleSwitchToCreatePatient = (phoneNumber: string) => {
    // Close the search modal
    setPatientSearchModalOpen(false);

    // Open the patient form with the phone number pre-filled
    setSearchParams({ addPatient: 'true', prefillPhone: phoneNumber });
  };

  // Handle Start Consultation directly from Patient Search Modal
  const handleStartConsultationFromSearch = async (patient: PatientData) => {
    if (!user?.id) {
      toast.error('User not authenticated');
      return;
    }

    // Prevent multiple simultaneous requests
    if (isCreatingEncounter) return;

    setIsCreatingEncounter('new-consultation');
    toast.info('Preparing consultation...', { duration: 2000 });

    try {
      const { encounterId } = await startConsultationForPatient(patient.uuid);

      queryClient.invalidateQueries({
        queryKey: ['dashboard-practitioner-appointments'],
        exact: false,
      });
      queryClient.invalidateQueries({ queryKey: ['appointments'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['dashboard-patients'], exact: false });

      toast.success('Ready for consultation');
      navigate(`/consultation/${encounterId}`);
    } catch (error) {
      logger.error('Failed to start consultation from search:', error);
      toast.error('Failed to start consultation. Please try again.');
    } finally {
      setIsCreatingEncounter(null);
    }
  };

  // Build statistics object for DashboardView
  const statistics: DashboardStatistics = {
    totalConsultations: todayStatsData?.totalConsultations ?? 0,
    newConsultations: todayStatsData?.newConsultations ?? 0,
    followUpVisits: todayStatsData?.followUpVisits ?? 0,
    isLoading: isLoadingTodayStats,
    error: isTodayStatsError
      ? todayStatsError instanceof Error
        ? todayStatsError.message
        : 'Failed to load statistics'
      : null,
    selectedCard: selectedStatCard,
    onCardSelect: setSelectedStatCard,
  };

  return (
    <AppLayout>
      <DashboardView
        appointments={{
          data: displayedAppointmentRows,
          isLoading: isFetching,
          error: isError ? (error instanceof Error ? error.message : 'Failed to load data') : null,
        }}
        statistics={statistics}
        search={{
          value: searchQuery,
          onChange: setSearchQuery,
        }}
        doctorFilter={
          effectiveShowDoctorFilter
            ? {
                value: selectedDoctorId,
                onChange: (v) => {
                  setSelectedDoctorId(v);
                  setCurrentPage(1);
                },
                practitioners: (practitionersData?.practitioners || []).map((p: any) => {
                  const name = p.name?.[0];
                  const displayName = name
                    ? `${name.given?.join(' ') || ''} ${name.family || ''}`.trim()
                    : p.id || 'Unknown';
                  return { value: p.id || '', label: displayName };
                }),
              }
            : undefined
        }
        showDoctorColumn={effectiveShowDoctorColumn}
        actions={{
          onRecordClick: handleRecordClick,
          onViewDetails: handleViewDetails,
          onViewReport: handleViewReport,
          onVerify: handleVerify,
          onStartConsultation: handleStartConsultation,
          onContinueConsultation: handleContinueConsultation,
          onAddPatient: projectConfig?.services.addPatient !== false ? handleAddPatient : undefined,
          onAddAppointment:
            projectConfig?.services.addAppointment !== false ? handleAddAppointment : undefined,
          onDeleteAppointment: handleDeleteAppointment,
          onAppointmentUpdated: handleAppointmentUpdated,
        }}
        isStartingConsultation={isCreatingEncounter}
        isContinuingConsultation={isContinuingConsultation}
        showConsultationButton={canConsult}
        pagination={
          totalPages > 1
            ? {
                currentPage,
                totalPages,
                onPageChange: setCurrentPage,
              }
            : undefined
        }
        onRefresh={() => {
          queryClient.invalidateQueries({
            queryKey: ['dashboard-practitioner-appointments'],
            exact: false,
          });
          queryClient.invalidateQueries({ queryKey: ['appointments'], exact: false });
        }}
        adminStatistics={adminStatistics || null}
        isAdminStatsLoading={isAdminStatsLoading}
        adminStatsError={adminStatsError?.message || null}
        onAdminStatsRefresh={() => refetchAdminStats()}
        adminStartDate={adminStartDate}
        adminEndDate={adminEndDate}
        onAdminStartDateChange={setAdminStartDate}
        onAdminEndDateChange={setAdminEndDate}
      />

      {/* Patient Form Modal - Add Patient only */}
      {addPatientFromQuery === 'true' && (
        <PatientFormModal isOpen={true} onClose={handleCloseFormModal}>
          <PatientFormContent
            patientId={isEditModeInModal ? patientIdFromQuery : undefined}
            onSuccess={handleFormSuccess}
            onCancel={handleCloseFormModal}
            isModal={true}
            onSwitchToAppointment={handleSwitchToAppointment}
            prefillPhone={prefillPhoneFromQuery || undefined}
          />
        </PatientFormModal>
      )}

      {/* Patient Search Modal - Step 1: Find patient by phone */}
      <PatientSearchModal
        isOpen={patientSearchModalOpen}
        onClose={() => setPatientSearchModalOpen(false)}
        onPatientSelected={handlePatientSelected}
        title="Create Appointment"
        description="Search for a patient by phone number to schedule an appointment"
        onSwitchToCreatePatient={handleSwitchToCreatePatient}
        onStartConsultation={canConsult ? handleStartConsultationFromSearch : undefined}
        isStartingConsultation={!!isCreatingEncounter}
      />

      {/* Schedule Appointment Modal */}
      <ScheduleAppointmentModal
        isOpen={!!scheduleModalPatient}
        onClose={() => setScheduleModalPatient(null)}
        patient={scheduleModalPatient}
        onAppointmentCreated={handleAppointmentCreated}
      />
    </AppLayout>
  );
}

/**
 * Map FHIR appointment status to display status
 * Simplified - no date-based logic
 */
function mapAppointmentStatusToDisplay(
  status?: string
): 'Waiting' | 'In Progress' | 'Consultation In-Progress' | 'Completed' | 'Upcoming' {
  switch (status?.toLowerCase()) {
    case 'pending':
      return 'Consultation In-Progress';
    case 'arrived':
      return 'Waiting';
    case 'fulfilled':
      return 'Completed';
    case 'cancelled':
    case 'noshow':
      return 'Completed';
    case 'booked':
    case 'proposed':
    case 'waitlist':
    default:
      return 'Waiting';
  }
}

function mapAppointmentTypeToVisitType(
  appointmentType?: string
): 'Consultation' | 'Follow-up' | 'Emergency' {
  if (!appointmentType) return 'Consultation';

  const type = appointmentType.toLowerCase();
  if (type.includes('follow') || type.includes('followup')) {
    return 'Follow-up';
  }
  if (type.includes('emergency') || type.includes('urgent')) {
    return 'Emergency';
  }
  return 'Consultation';
}

export default DashboardPage;
